import { useMemo, useState } from 'react'
import { MAX_AI_CHAT_VISITED_LOCATIONS } from '@amitable/shared/ai-chat-navigation'
import type { AiChatPageContext } from './ai-chat-navigation-context'
import type { AiChatNavigationLocation } from '@amitable/shared/ai-chat-navigation'

function decodeNavigationId(id?: string) {
  if (!id) return undefined
  try {
    return decodeURIComponent(id)
  } catch {
    return id
  }
}

export function getAiChatNavigationLocation(
  pathname: string,
): AiChatNavigationLocation {
  const parts = pathname.split('/')
  const id = decodeNavigationId(parts[2])

  return {
    pathname,
    documentCollectionId: parts[1] === 'document-collection' ? id : undefined,
    recordId: parts[1] === 'document' ? id : undefined,
  }
}

export function addAiChatNavigationLocation(
  visited: Array<AiChatNavigationLocation>,
  current: AiChatNavigationLocation,
) {
  const previousIndex = visited.findIndex(
    (location) =>
      location.pathname === current.pathname &&
      location.documentCollectionId === current.documentCollectionId &&
      location.recordId === current.recordId,
  )
  if (previousIndex === visited.length - 1 && previousIndex >= 0) return visited

  return [
    ...visited.filter((_, index) => index !== previousIndex),
    current,
  ].slice(-MAX_AI_CHAT_VISITED_LOCATIONS)
}

export function useAiChatNavigation(pathname: string) {
  const [pageContext, setPageContext] = useState<{
    pathname: string
    details: AiChatPageContext
  } | null>(null)
  const details =
    pageContext?.pathname === pathname ? pageContext.details : undefined
  const current = useMemo(
    () => ({
      ...getAiChatNavigationLocation(pathname),
      ...(details
        ? {
            documentCollectionId: details.documentCollectionId,
            recordId: details.recordId,
          }
        : {}),
    }),
    [pathname, details],
  )
  const [history, setHistory] = useState(() => ({
    current,
    visited: [current],
  }))
  const visited = addAiChatNavigationLocation(history.visited, current)
  if (history.current !== current) setHistory({ current, visited })

  return {
    context: {
      current,
      visited,
    },
    label: details?.label,
    setPageContext,
  }
}
